/**
 * Canvas event handlers for panning, resizing, and mouse interactions
 * Extracted from Canvas.jsx to reduce file size
 */
import { FIXED_CANVAS_STAGE_HEIGHT_PX } from "./mainCanvasLayout";

/**
 * Finds the nearest scrollable parent container
 */
const findScrollableParent = (element) => {
  if (!element) return null;
  
  let parent = element.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflow = style.overflow + style.overflowX + style.overflowY;
    if (overflow.includes('auto') || overflow.includes('scroll')) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
};

/**
 * Creates mouse event handlers for canvas panning
 */
export const createPanHandlers = ({
  spacePressed,
  action,
  ACTIONS,
  setIsPanning,
  setLastPanPos,
  setStagePos,
  lastPanPos,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  stageContainerRef,
  scrollAreaRef,
  panStateRef,
  stageRef,
}) => {
  const applyPanDelta = (dx, dy) => {
    if (panStateRef && panStateRef.current.scrollableParent) {
      panStateRef.current.scrollableParent.scrollLeft -= dx;
      panStateRef.current.scrollableParent.scrollTop -= dy;
    }
  };

  const detachWindowPanListeners = () => {
    if (!panStateRef?.current) return;
    if (panStateRef.current.windowMouseMoveHandler) {
      window.removeEventListener(
        "mousemove",
        panStateRef.current.windowMouseMoveHandler
      );
      panStateRef.current.windowMouseMoveHandler = null;
    }
    if (panStateRef.current.windowMouseUpHandler) {
      window.removeEventListener("mouseup", panStateRef.current.windowMouseUpHandler);
      panStateRef.current.windowMouseUpHandler = null;
    }
  };

  return {
    handleStageMouseDown: (e) => {
      // Start panning if middle mouse, space+left mouse, or PAN action is selected
      const shouldPan = 
        e.evt.button === 1 || 
        (spacePressed && e.evt.button === 0) ||
        (action === ACTIONS?.PAN && e.evt.button === 0);
      
      if (shouldPan) {
        // Stop event propagation to prevent element handlers from firing
        e.evt.preventDefault();
        e.evt.stopPropagation();
        setIsPanning(true);
        setLastPanPos({ x: e.evt.clientX, y: e.evt.clientY });
        setStagePos({ x: 0, y: 0 });
        document.body.style.cursor = "grab";
        
        // Find scrollable parent and store initial scroll position
        if (panStateRef) {
          // Prefer explicit scroll container for reliable pan/scroll sync.
          const explicitScrollParent = scrollAreaRef?.current || null;
          panStateRef.current.scrollableParent =
            explicitScrollParent ||
            (stageContainerRef?.current
              ? findScrollableParent(stageContainerRef.current)
              : null);
          if (panStateRef.current.scrollableParent) {
            panStateRef.current.initialScrollLeft = panStateRef.current.scrollableParent.scrollLeft;
            panStateRef.current.initialScrollTop = panStateRef.current.scrollableParent.scrollTop;
          }
        }
        
        // Reset accumulated pan
        if (panStateRef) {
          panStateRef.current.accumulatedPanX = 0;
          panStateRef.current.accumulatedPanY = 0;
          panStateRef.current.lastClientX = e.evt.clientX;
          panStateRef.current.lastClientY = e.evt.clientY;
        }

        // Continue panning even when pointer leaves the stage.
        if (panStateRef?.current) {
          detachWindowPanListeners();
          panStateRef.current.windowMouseMoveHandler = (moveEvent) => {
            const prevX = panStateRef.current.lastClientX;
            const prevY = panStateRef.current.lastClientY;
            const dx = moveEvent.clientX - prevX;
            const dy = moveEvent.clientY - prevY;
            applyPanDelta(dx, dy);
            panStateRef.current.lastClientX = moveEvent.clientX;
            panStateRef.current.lastClientY = moveEvent.clientY;
            setLastPanPos({ x: moveEvent.clientX, y: moveEvent.clientY });
            document.body.style.cursor = "grabbing";
          };
          panStateRef.current.windowMouseUpHandler = () => {
            setIsPanning(false);
            setStagePos({ x: 0, y: 0 });
            document.body.style.cursor = "default";
            detachWindowPanListeners();
            panStateRef.current.scrollableParent = null;
            panStateRef.current.initialScrollLeft = 0;
            panStateRef.current.initialScrollTop = 0;
            panStateRef.current.accumulatedPanX = 0;
            panStateRef.current.accumulatedPanY = 0;
          };
          window.addEventListener(
            "mousemove",
            panStateRef.current.windowMouseMoveHandler,
            { passive: true }
          );
          window.addEventListener("mouseup", panStateRef.current.windowMouseUpHandler);
        }
      } else if (onPointerDown) {
        onPointerDown(e);
      }
    },

    handleStageMouseMove: (e, isPanningValue) => {
      if (isPanningValue) {
        // Stop event propagation to prevent element handlers from firing during panning
        e.evt.preventDefault();
        e.evt.stopPropagation();
        const dx = e.evt.clientX - lastPanPos.x;
        const dy = e.evt.clientY - lastPanPos.y;

        // Keep content alignment static while hand tool pans by scrolling only.
        applyPanDelta(dx, dy);

        setLastPanPos({ x: e.evt.clientX, y: e.evt.clientY });
        if (panStateRef?.current) {
          panStateRef.current.lastClientX = e.evt.clientX;
          panStateRef.current.lastClientY = e.evt.clientY;
        }
        document.body.style.cursor = "grabbing";
      } else if (onPointerMove) {
        onPointerMove(e);
      }
    },

    handleStageMouseUp: (e, isPanningValue) => {
      if (isPanningValue) {
        // Stop event propagation to prevent element handlers from firing
        e.evt.preventDefault();
        e.evt.stopPropagation();

        // Keep the current visual pan position after pointer release.
        // Stage remains fixed; panning is represented by scrollbar position.
        setIsPanning(false);
        setStagePos({ x: 0, y: 0 });
        document.body.style.cursor = "default";
        detachWindowPanListeners();

        // Reset tracking variables
        if (panStateRef) {
          panStateRef.current.scrollableParent = null;
          panStateRef.current.initialScrollLeft = 0;
          panStateRef.current.initialScrollTop = 0;
          panStateRef.current.accumulatedPanX = 0;
          panStateRef.current.accumulatedPanY = 0;
        }
      } else if (onPointerUp) {
        onPointerUp(e);
      }
    },

    handleStageMouseLeave: (isPanningValue) => {
      if (isPanningValue) {
        // Keep stage fixed on leave as well.
        setStagePos({ x: 0, y: 0 });
        document.body.style.cursor = "grabbing";
      }
    },
  };
};

/**
 * Creates resize handlers for canvas height adjustment
 */
export const createResizeHandlers = ({
  canvasHeight,
  setCanvasHeight,
  applyCanvasHeight,
  getMinCanvasHeightPercent,
  setCanvasResizeTrigger,
  stageRef,
  saveToHistory,
}) => {
  // Store last applied percent so resize-end save uses correct height (avoids stale closure)
  const lastAppliedPercentRef = { current: null };

  return {
    handleResizeMouseDown: (
      e,
      setIsResizing,
      setResizeStartY,
      setInitialHeight
    ) => {
      setIsResizing(true);
      setResizeStartY(e.evt.clientY);
      setInitialHeight(
        (parseInt(canvasHeight) / 100) * FIXED_CANVAS_STAGE_HEIGHT_PX,
      );
      document.body.style.cursor = "ns-resize";
    },

    handleResizeMouseMove: (e, isResizing, resizeStartY, initialHeight) => {
      if (!isResizing) return;
      const deltaY = e.evt.clientY - resizeStartY;
      let newHeight = initialHeight + deltaY;
      const maxH = FIXED_CANVAS_STAGE_HEIGHT_PX;
      newHeight = Math.max(0, Math.min(newHeight, maxH));
      const newHeightPercent = (newHeight / maxH) * 100;
      const sanitizedPercent = Math.min(100, Math.max(0, newHeightPercent));

      let appliedPercent = sanitizedPercent;

      // Execute synchronously for immediate feedback, but wrap expensive operations
      if (typeof applyCanvasHeight === "function") {
        const result = applyCanvasHeight(sanitizedPercent);
        if (typeof result === "number" && !Number.isNaN(result)) {
          appliedPercent = result;
        }
      } else if (typeof canvasHeight === "function") {
        const result = canvasHeight(sanitizedPercent);
        if (typeof result === "number" && !Number.isNaN(result)) {
          appliedPercent = result;
        }
      } else if (typeof setCanvasHeight === "function") {
        // Throttle getMinCanvasHeightPercent call - it's expensive with many elements
        // Use try-catch to prevent blocking if calculation is too expensive
        let minPercent = 0;
        if (typeof getMinCanvasHeightPercent === "function") {
          try {
            minPercent = Math.min(
              100,
              Math.max(0, Math.ceil(getMinCanvasHeightPercent() || 0))
            );
          } catch (err) {
            // Fallback if calculation fails (e.g., too many elements)
            console.warn("Canvas height calculation failed, using 0 as minimum:", err);
            minPercent = 0;
          }
        }
        appliedPercent = Math.max(minPercent, sanitizedPercent);
        setCanvasHeight(String(appliedPercent));
      }

      lastAppliedPercentRef.current = appliedPercent;

      // Defer non-critical operations to next frame to prevent blocking
      requestAnimationFrame(() => {
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("canvas-resize", { detail: appliedPercent })
          );
        }
        // Force re-render of text elements to update HTML content width
        if (setCanvasResizeTrigger) {
          setCanvasResizeTrigger((prev) => prev + 1);
        }
        if (stageRef?.current) {
          stageRef.current.batchDraw();
        }
      });
    },

    handleResizeMouseUp: (setIsResizing) => {
      setIsResizing(false);
      document.body.style.cursor = "default";

      const finalPercent = lastAppliedPercentRef.current;
      lastAppliedPercentRef.current = null;

      // Save to history when resize is complete (pass height so history has correct value)
      if (typeof saveToHistory === "function") {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            saveToHistory(
              "canvas_height_change",
              null, null, null, null, null, null, null, null, null, null,
              null, null, null, null, null, null, null, null,
              finalPercent != null ? { currentCanvasHeightOverride: finalPercent } : undefined
            );
          });
        });
      }
    },
  };
};
